import logger from './logger';

export const scrollYByElement = (el: HTMLElement | Window, offsetTop: number, isSmooth: boolean = false): void => {
  el.scroll({
    top: offsetTop,
    left: 0,
    behavior: isSmooth ? 'smooth' : 'instant',
  });
};

export const offset = (el: HTMLElement | null): { top: number; left: number } => {
  const rect = el?.getBoundingClientRect();
  const { scrollLeft, scrollTop } = document.documentElement;

  return {
    top: (rect?.top ?? 0) + scrollTop,
    left: (rect?.left ?? 0) + scrollLeft,
  };
};

/**
 * Sets custom CSS variables on the specified DOM element.
 * @param DOMElement - The DOM element to set CSS variables on.
 * @param colorsCollection - An object containing CSS variable names as keys and their values.
 * @returns The DOM element with applied CSS variables.
 */
export const setCustomCssVariables = (
  DOMElement: HTMLElement,
  colorsCollection: Record<string, string | null>,
): void => {
  Object.entries(colorsCollection).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      DOMElement.style.setProperty(key, value);
    } else {
      DOMElement.style.removeProperty(key);
    }
  });
};

export const createTag = <K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  props: Partial<HTMLElementTagNameMap[K]> = {},
  attrs: Record<string, string | number | boolean> = {},
): HTMLElementTagNameMap[K] => {
  const tag = document.createElement(tagName);

  Object.entries(props).forEach(([key, value]) => {
    (tag as HTMLElementTagNameMap[K])[key as keyof HTMLElementTagNameMap[K]] = value;
  });

  Object.entries(attrs).forEach(([key, value]) => {
    tag.setAttribute(key, `${value}`);
  });

  return tag;
};

const mountedTagList: HTMLElement[] = [];

export const mountTag = (
  tagName: keyof HTMLElementTagNameMap,
  {
    props = {},
    attrs = {},
    appendContainer = document.head,
    withoutUnmount = false,
  }: {
    props?: Record<string, string | number | boolean>;
    attrs?: Record<string, string | number | boolean>;
    appendContainer?: HTMLElement | Element | ShadowRoot;
    withoutUnmount?: boolean;
  },
): HTMLElement => {
  const tag = createTag(tagName, props, attrs);

  if (!withoutUnmount) {
    mountedTagList.push(tag);
  }

  appendContainer.appendChild(tag);

  return tag;
};

export const unmountTag = (domElementReferrer?: HTMLElement): void => {
  domElementReferrer?.remove();
};

export const unmountAllTags = (): void => {
  mountedTagList.forEach(unmountTag);
  mountedTagList.length = 0; // Clear the mountedTagList array after unmounting
};

let publisherAnchorElement: Element | null = null;

export const handlePublisherAnchor = (): void => {
  const anchorId = window.location.hash.trim();

  if (anchorId && !/^#\//.test(anchorId)) {
    try {
      publisherAnchorElement = document.querySelector(anchorId);
    } catch {
      logger.warn(`${anchorId} doesn't exist`);
    }
  }
};

export const scrollToPublisherAnchor = (): void => {
  window.setTimeout(() => {
    if (publisherAnchorElement) {
      publisherAnchorElement.scrollIntoView(true);
      publisherAnchorElement = null;
    }
  }, 200);
};

export const hasPublisherAnchor = (): boolean => !!publisherAnchorElement;

export function runWhenDOMReady(callback: () => void) {
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    callback();
  } else {
    window.addEventListener('DOMContentLoaded', callback, { once: true });
  }
}
